import { dehydrate, QueryClient } from '@tanstack/react-query';
import Image from 'next/image';
import { useState } from 'react';
import Fade from 'react-reveal/Fade';

import EventsTicker from '@/components/Core/EventsTicker/EventsTicker';
import GameCard from '@/components/Core/GameCard/GameCard';
import SectionTitle from '@/components/Core/SectionTitle/SectionTitle';
import MainLayout from '@/components/Layout/Main';
import { CustomMeta } from '@/components/Meta/CustomMeta';
import SelectDropdown from '@/components/Primitive/SelectDropdown/SelectDropdown';
import Spinner from '@/components/Primitive/Spinner/Spinner';
import useQuery, { makeQuery } from '@/hooks/use-query';
import { prefetchEvents } from '@/lib/prefetch-events';
import {
  createGameGeneresQuery,
  mapToGameGeneres,
} from '@/queries/games/genres';
import { createGamesListQuery, mapToGamesList } from '@/queries/games/list';
import {
  createHybridGamesQuery,
  mapToHybridGamesContent,
} from '@/queries/globals/hybrid-games';

const hybridGamesContentQuery = createHybridGamesQuery();
const gamesListQuery = createGamesListQuery();
const gameGenresQuery = createGameGeneresQuery();

const HybridGames = () => {
  const { data: genres } = useQuery(gameGenresQuery, {}, mapToGameGeneres);
  const { data: pageData } = useQuery(
    hybridGamesContentQuery,
    {},
    mapToHybridGamesContent
  );

  const genreOptions = genres?.map((genre) => ({
    name: genre.name,
    value: genre.id,
  }));
  const defaultOption = { name: 'All', value: '' };
  const options = [defaultOption, ...(genreOptions || [])];

  const [sort, setSort] = useState<{ name: string; value: string }>(
    defaultOption
  );

  const gamesListVars = {
    ...(sort.value && { genreId: sort.value }),
  };

  const { data, isLoading: loading } = useQuery(
    gamesListQuery,
    gamesListVars,
    mapToGamesList
  );
  const { games } = data || {};

  return (
    <MainLayout className="pt-14 md:pt-[120px]">
      <CustomMeta {...pageData?.seo} />
      <div className="pb-page space-y-page mx-auto w-full overflow-hidden">
        <div>
          {Number(process.env.ENABLE_EVENTS_TICKER) === 1 && (
            <EventsTicker className="hidden md:block" />
          )}
          <div className="relative aspect-[5/3] md:aspect-[5/2]">
            {pageData?.heroImage && (
              <Image
                src={pageData.heroImage}
                fill
                alt=""
                className="object-cover object-center"
                loading="eager"
                sizes="(min-width: 768px) 100vw, 100vw"
              />
            )}
          </div>
        </div>

        <div className="container-md flex flex-col items-start">
          <SectionTitle
            className="!items-start !justify-start !text-left"
            intro="HYBRID"
            title="GAMES"
          />

          <div className="flex w-full flex-wrap items-center justify-between gap-5">
            {pageData?.description && (
              <p className="max-w-[400px] text-lg text-grayAlt-500">
                {pageData.description}
              </p>
            )}
            <SelectDropdown
              className="ml-auto shrink-0"
              label="Filter by:"
              options={options}
              onChange={setSort}
              current={sort}
            />
          </div>
        </div>
        <div className="container-base !mt-4 md:!mt-6">
          <div className="flex w-full flex-col gap-10 sm:gap-12 lg:gap-14">
            {games?.map((game) => (
              <Fade bottom key={game.id}>
                <GameCard key={game.id} {...game} href="/play-now" />
              </Fade>
            ))}
          </div>

          {loading && (
            <div className="flex h-[350px] w-full items-center justify-center">
              <Spinner className="!h-14 !w-14 text-cream" />
            </div>
          )}
        </div>

        <div className="container-sm flex flex-col items-center justify-center text-center">
          <div className="font-orton text-3xl text-cream md:text-4xl">
            More games coming soon!
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default HybridGames;

export async function getStaticProps() {
  const queryClient = new QueryClient();
  await prefetchEvents(queryClient);

  await queryClient.prefetchQuery(
    ['query', hybridGamesContentQuery, {}],
    async () => {
      return makeQuery(hybridGamesContentQuery, {}, mapToHybridGamesContent);
    }
  );

  await queryClient.prefetchQuery(['query', gameGenresQuery, {}], async () => {
    return makeQuery(gameGenresQuery, {}, mapToGameGeneres);
  });

  const gamesListVars = {};

  await queryClient.prefetchQuery(
    ['query', gamesListQuery, gamesListVars],
    async () => {
      return makeQuery(gamesListQuery, gamesListVars, mapToGamesList);
    }
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 60,
  };
}
